import React from 'react';
import { FONTS } from '../../styles/tokens';
import { APP_CATEGORIES } from '../../data/app-categories';
import { DeployDropZone } from './DeployDropZone';
import { IconUploader } from './IconUploader';

interface ThemeColors {
  text: string;
  textSecondary: string;
  border: string;
  card: string;
}

interface DetailsCardProps {
  colors: ThemeColors;
  isDark: boolean;
  name: string; setName: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  team: string; setTeam: (v: string) => void;
  category: string; setCategory: (v: string) => void;
  icon: File | null; setIcon: (file: File | null) => void;
  onNext: () => void;
}

/** Step 1 card: name + optional metadata + optional icon upload. */
export function DetailsCard(props: DetailsCardProps): React.JSX.Element {
  const canContinue = props.name.trim().length > 0;
  return (
    <Card colors={props.colors}>
      <FieldLabel colors={props.colors}>App name</FieldLabel>
      <input
        value={props.name}
        onChange={e => props.setName(e.target.value)}
        placeholder="e.g. Vertex Hello World"
        style={inputStyle(props.colors, props.isDark)}
      />
      <div className="vx-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
        <div>
          <FieldLabel colors={props.colors}>Team (optional)</FieldLabel>
          <input value={props.team} onChange={e => props.setTeam(e.target.value)} placeholder="Velocity" style={inputStyle(props.colors, props.isDark)} />
        </div>
        <div>
          <FieldLabel colors={props.colors}>Category</FieldLabel>
          <select
            value={props.category}
            onChange={e => props.setCategory(e.target.value)}
            style={inputStyle(props.colors, props.isDark)}
          >
            {APP_CATEGORIES.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginTop: '16px' }}>
        <FieldLabel colors={props.colors}>Description (optional)</FieldLabel>
        <input value={props.description} onChange={e => props.setDescription(e.target.value)} placeholder="One-line description" style={inputStyle(props.colors, props.isDark)} />
      </div>
      <div style={{ marginTop: '16px' }}>
        <FieldLabel colors={props.colors}>App icon (optional)</FieldLabel>
        <IconUploader isDark={props.isDark} colors={props.colors} onChange={props.setIcon} />
      </div>
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryButton disabled={!canContinue} onClick={props.onNext} isDark={props.isDark}>Next: Upload</PrimaryButton>
      </div>
    </Card>
  );
}

/** Step 2 card: drag-drop zone. */
export function UploadCard(props: {
  colors: ThemeColors;
  isDark: boolean;
  fileTooLarge: boolean;
  maxBytes: number;
  onFileSelected: (file: File) => void;
  onBack: () => void;
}): React.JSX.Element {
  const maxMb = Math.floor(props.maxBytes / 1024 / 1024);
  return (
    <Card colors={props.colors}>
      <DeployDropZone
        sizeHint={`Max ${maxMb} MB`}
        isDark={props.isDark}
        border={props.colors.border}
        textPrimary={props.colors.text}
        textSecondary={props.colors.textSecondary}
        accent="#01A9ED"
        onFileSelected={props.onFileSelected}
      />
      {props.fileTooLarge && (
        <div style={{ marginTop: '12px', fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444' }}>
          File exceeds the {maxMb}MB limit.
        </div>
      )}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-start' }}>
        <SecondaryButton colors={props.colors} onClick={props.onBack}>Back</SecondaryButton>
      </div>
    </Card>
  );
}

/** Step 3 card: review + deploy. */
export function ReviewCard(props: {
  colors: ThemeColors;
  isDark: boolean;
  name: string; description: string; team: string; category: string;
  file: File;
  building: boolean;
  error: string | null;
  onDeploy: () => void;
  onChangeFile: () => void;
  onEditDetails: () => void;
}): React.JSX.Element {
  const sizeMb = (props.file.size / 1024 / 1024).toFixed(2);
  return (
    <Card colors={props.colors}>
      <ReviewRow colors={props.colors} label="Name" value={props.name} onEdit={props.onEditDetails} />
      {props.description && <ReviewRow colors={props.colors} label="Description" value={props.description} onEdit={props.onEditDetails} />}
      <ReviewRow colors={props.colors} label="Team" value={props.team || '—'} onEdit={props.onEditDetails} />
      <ReviewRow colors={props.colors} label="Category" value={props.category} onEdit={props.onEditDetails} />
      <ReviewRow colors={props.colors} label="Archive" value={`${props.file.name} (${sizeMb} MB)`} onEdit={props.onChangeFile} editLabel="Change" />
      {props.error && (
        <div style={{ marginTop: '12px', fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444' }}>{props.error}</div>
      )}
      <div className="vx-action-row" style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <SecondaryButton colors={props.colors} onClick={props.onEditDetails} disabled={props.building}>Edit details</SecondaryButton>
        <PrimaryButton onClick={props.onDeploy} disabled={props.building} isDark={props.isDark}>
          {props.building ? 'Deploying…' : `Deploy ${props.name}`}
        </PrimaryButton>
      </div>
    </Card>
  );
}

/** One labelled row inside the review card with an edit affordance. */
function ReviewRow(props: {
  colors: ThemeColors;
  label: string;
  value: string;
  onEdit: () => void;
  editLabel?: string;
}): React.JSX.Element {
  return (
    <div className="vx-review-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', alignItems: 'center', gap: '12px', padding: '6px 0' }}>
      <div style={{ fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: props.colors.textSecondary }}>
        {props.label}
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: props.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {props.value}
      </div>
      <button onClick={props.onEdit} style={{ border: 'none', background: 'transparent', color: '#01A9ED', fontFamily: FONTS.body, fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>
        {props.editLabel ?? 'Edit'}
      </button>
    </div>
  );
}

/** Neutral card container used by each step. */
function Card(props: { children: React.ReactNode; colors: ThemeColors }): React.JSX.Element {
  return (
    <div style={{ padding: '24px', borderRadius: '12px', border: `1px solid ${props.colors.border}`, backgroundColor: props.colors.card }}>
      {props.children}
    </div>
  );
}

/** Small uppercase label above a form field. */
function FieldLabel(props: { children: React.ReactNode; colors: { textSecondary: string } }): React.JSX.Element {
  return (
    <div style={{ fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: props.colors.textSecondary, marginBottom: '6px' }}>
      {props.children}
    </div>
  );
}

/** Primary call-to-action button (gradient). */
export function PrimaryButton(props: { children: React.ReactNode; disabled?: boolean; onClick: () => void; isDark: boolean }): React.JSX.Element {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      style={{
        padding: '10px 22px',
        borderRadius: '8px',
        border: 'none',
        background: props.disabled
          ? '#3a3f47'
          : props.isDark
            ? 'linear-gradient(90deg, #0266C6, #00407D)'
            : 'linear-gradient(90deg, #01A9ED, #0165C6)',
        color: '#FFF',
        fontFamily: FONTS.heading,
        fontSize: '12px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {props.children}
    </button>
  );
}

/** Outline secondary button (Back / Edit). */
export function SecondaryButton(props: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
  colors: { text: string; border: string };
}): React.JSX.Element {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      style={{
        padding: '10px 22px',
        borderRadius: '8px',
        border: `1px solid ${props.colors.border}`,
        background: 'transparent',
        color: props.colors.text,
        fontFamily: FONTS.heading,
        fontSize: '12px',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {props.children}
    </button>
  );
}

/** Shared input style used by the detail-card fields. */
function inputStyle(colors: { border: string; text: string }, isDark: boolean): React.CSSProperties {
  return {
    width: '100%',
    height: '40px',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    color: colors.text,
    fontFamily: FONTS.body,
    fontSize: '13px',
    outline: 'none',
  };
}
